import HMOApi, {AI_TOKEN_USAGE, CLINICAL_REPORT_ANONYMIZATION_GLOBAL_SETTING} from "store/api/HMOApi";
import {AxiosClient} from "configs/Axios";

const BASE_URL = "/doctors-letter";

export const DoctorsLetterApi = HMOApi.injectEndpoints({
    endpoints: (build) => ({
        summarizeClinicalReports: build.mutation({
            async queryFn({currentReportFiles, comparisonReportFiles, patientAge, patientSex, summaryMode, comparisonOptions, sourceDocumentsConfirmationText}) {
                try {
                    const formData = new FormData();
                    currentReportFiles.forEach((currentReportFile) => formData.append("currentFiles", currentReportFile));
                    comparisonReportFiles.forEach((comparisonReportFile) => formData.append("comparisonFiles", comparisonReportFile));
                    formData.append("age", patientAge);
                    formData.append("sex", patientSex);
                    formData.append("mode", summaryMode);
                    comparisonOptions.forEach((comparisonOption) => formData.append("comparisonOptions", comparisonOption));
                    formData.append("sourceDocumentsConfirmationText", sourceDocumentsConfirmationText);
                    const response = await AxiosClient({
                        url: `${BASE_URL}/summarize-clinical-reports`,
                        method: "POST",
                        data: formData,
                        headers: {"Content-Type": "multipart/form-data"},
                    });
                    return {data: response.data};
                } catch (error) {
                    return {error: {status: error.response?.status, data: error.response?.data}};
                }
            },
            invalidatesTags: [AI_TOKEN_USAGE],
        }),
        saveClinicalReportCopyConfirmation: build.mutation({
            async queryFn({confirmationTexts, fileNames, summaryHash}) {
                try {
                    const response = await AxiosClient({
                        url: `${BASE_URL}/confirmations/copy`,
                        method: "POST",
                        data: {confirmationTexts, fileNames, summaryHash},
                    });
                    return {data: response.data};
                } catch (error) {
                    return {error: {status: error.response?.status, data: error.response?.data}};
                }
            },
        }),
        submitClinicalReportAnonymization: build.mutation({
            async queryFn(clinicalReportFile) {
                try {
                    const formData = new FormData();
                    formData.append("file", clinicalReportFile);
                    const response = await AxiosClient({
                        url: `${BASE_URL}/anonymization`,
                        method: "POST",
                        data: formData,
                        headers: {"Content-Type": "multipart/form-data"},
                    });
                    return {data: response.data};
                } catch (error) {
                    return {error: {status: error.response?.status, data: error.response?.data}};
                }
            },
        }),
        getClinicalReportSummaryAvailability: build.query({
            query: () => ({
                url: `${BASE_URL}/clinical-report-summary/availability`,
                method: "GET",
            }),
        }),
        getClinicalReportAnonymizationGlobalSetting: build.query({
            query: () => ({
                url: `${BASE_URL}/anonymization/global-setting`,
                method: "GET",
            }),
            providesTags: [CLINICAL_REPORT_ANONYMIZATION_GLOBAL_SETTING],
        }),
        saveClinicalReportAnonymizationGlobalSetting: build.mutation({
            async queryFn(isGlobalAnonymizationEnabled) {
                try {
                    const response = await AxiosClient({
                        url: `${BASE_URL}/anonymization/global-setting`,
                        method: "PUT",
                        data: JSON.stringify(isGlobalAnonymizationEnabled),
                        headers: {"Content-Type": "application/json"},
                    });
                    return {data: response.data};
                } catch (error) {
                    return {error: {status: error.response?.status, data: error.response?.data}};
                }
            },
            invalidatesTags: [CLINICAL_REPORT_ANONYMIZATION_GLOBAL_SETTING],
        }),
        getClinicalReportAnonymizationJob: build.mutation({
            async queryFn(anonymizationJobId) {
                try {
                    const response = await AxiosClient({
                        url: `${BASE_URL}/anonymization/${encodeURIComponent(anonymizationJobId)}`,
                        method: "GET",
                    });
                    return {data: response.data};
                } catch (error) {
                    return {error: {status: error.response?.status, data: error.response?.data}};
                }
            },
        }),
        downloadAnonymizedClinicalReport: build.mutation({
            async queryFn(anonymizationJobId) {
                try {
                    const response = await AxiosClient({
                        url: `${BASE_URL}/anonymization/${encodeURIComponent(anonymizationJobId)}/document`,
                        method: "GET",
                        responseType: "blob",
                    });
                    return {data: response.data};
                } catch (error) {
                    return {error: {status: error.response?.status}};
                }
            },
        }),
    }),
});

export const {
    useSummarizeClinicalReportsMutation,
    useGetClinicalReportSummaryAvailabilityQuery,
    useSaveClinicalReportCopyConfirmationMutation,
    useSubmitClinicalReportAnonymizationMutation,
    useGetClinicalReportAnonymizationGlobalSettingQuery,
    useSaveClinicalReportAnonymizationGlobalSettingMutation,
    useGetClinicalReportAnonymizationJobMutation,
    useDownloadAnonymizedClinicalReportMutation,
} = DoctorsLetterApi;
